import { autorun } from "mobx";
import { locale, updateLocaleOptions } from "primereact/api";
import { useEffect, useRef } from "react";
import { LocStoreInstance, useLocStore } from "../wsLocalization";
import { LocContext } from "../wsLocalization/LocContext";
import { CvpLocale } from "./getIdLanguage";

// all prime components that use locale should be wrapped to observe
// useLocStore("primereact").gotTranslationsForLocale so they rerender
// when new translations are available
export const usePrimeLocale = () => {
  const L = useLocStore(LocContext.GENERAL);

  const lastSetLocale = useRef<CvpLocale>(null);

  useEffect(() => {
    return autorun(() => {
      let localeToLoad: CvpLocale;

      if (L.gotTranslationsForLocale) {
        localeToLoad = L.gotTranslationsForLocale;
      }

      if (localeToLoad && localeToLoad !== lastSetLocale.current) {
        updateLocaleOptions(getLocaleProperties(L), "_custom_primelocale_");
        locale("_custom_primelocale_");
        lastSetLocale.current = localeToLoad;
      }
    });
  }, []);
};

function getLocaleProperties(L: LocStoreInstance) {
  return {
    aria: {
      trueLabel: L.id("f504704ab08b").loc("Sant"),
      falseLabel: L.id("c4810069d07e").loc("Usant"),
      nullLabel: L.id("926b218c37ec").loc("Ikke valgt"),
      pageLabel: L.id("84c4e3bcfa19").loc("Side"),
      firstPageLabel: L.id("43d357c742d9").loc("Første side"),
      lastPageLabel: L.id("06ed628c725e").loc("Siste side"),
      nextPageLabel: L.id("b03a6e692487").loc("Neste side"),
      previousPageLabel: L.id("a5a98b435747").loc("Forrige side"),
    },

    firstDayOfWeek: 1,
    dateFormat: "dd.mm.yy",
    accept: L.id("prime_accept").loc("Ja"),
    reject: L.id("prime_reject").loc("Nei"),
    choose: L.id("prime_choose").loc("Velg"),
    upload: L.id("prime_upload").loc("Last opp"),
    cancel: L.id("prime_cancel").loc("Avbryt"),
    today: L.id("prime_today").loc("I dag"),
    clear: L.id("prime_clear").loc("Fjern"),
    weekHeader: L.id("prime_weekHeader").loc("Uke"),
    dayNames: [
      L.id("prime_dayNames_0").loc("Søndag"),
      L.id("prime_dayNames_1").loc("Mandag"),
      L.id("prime_dayNames_2").loc("Tirsdag"),
      L.id("prime_dayNames_3").loc("Onsdag"),
      L.id("prime_dayNames_4").loc("Torsdag"),
      L.id("prime_dayNames_5").loc("Fredag"),
      L.id("prime_dayNames_6").loc("Lørdag"),
    ],
    dayNamesShort: [
      L.id("prime_dayNamesShort_0").loc("Søn"),
      L.id("prime_dayNamesShort_1").loc("Man"),
      L.id("prime_dayNamesShort_2").loc("Tir"),
      L.id("prime_dayNamesShort_3").loc("Ons"),
      L.id("prime_dayNamesShort_4").loc("Tor"),
      L.id("prime_dayNamesShort_5").loc("Fre"),
      L.id("prime_dayNamesShort_6").loc("Lør"),
    ],
    dayNamesMin: [
      L.id("prime_dayNamesMin_0").loc("Sø"),
      L.id("prime_dayNamesMin_1").loc("Ma"),
      L.id("prime_dayNamesMin_2").loc("Ti"),
      L.id("prime_dayNamesMin_3").loc("On"),
      L.id("prime_dayNamesMin_4").loc("To"),
      L.id("prime_dayNamesMin_5").loc("Fr"),
      L.id("prime_dayNamesMin_6").loc("Lø"),
    ],
    monthNames: [
      L.id("prime_monthNames_0").loc("januar"),
      L.id("prime_monthNames_1").loc("februar"),
      L.id("prime_monthNames_2").loc("mars"),
      L.id("prime_monthNames_3").loc("april"),
      L.id("prime_monthNames_4").loc("mai"),
      L.id("prime_monthNames_5").loc("juni"),
      L.id("prime_monthNames_6").loc("juli"),
      L.id("prime_monthNames_7").loc("august"),
      L.id("prime_monthNames_8").loc("september"),
      L.id("prime_monthNames_9").loc("oktober"),
      L.id("prime_monthNames_10").loc("november"),
      L.id("prime_monthNames_11").loc("desember"),
    ],
    monthNamesShort: [
      L.id("prime_monthNamesShort_0").loc("jan"),
      L.id("prime_monthNamesShort_1").loc("feb"),
      L.id("prime_monthNamesShort_2").loc("mar"),
      L.id("prime_monthNamesShort_3").loc("apr"),
      L.id("prime_monthNamesShort_4").loc("mai"),
      L.id("prime_monthNamesShort_5").loc("jun"),
      L.id("prime_monthNamesShort_6").loc("jul"),
      L.id("prime_monthNamesShort_7").loc("aug"),
      L.id("prime_monthNamesShort_8").loc("sep"),
      L.id("prime_monthNamesShort_9").loc("okt"),
      L.id("prime_monthNamesShort_10").loc("nov"),
      L.id("prime_monthNamesShort_11").loc("des"),
    ],
  };
}
